import { useState, type ReactNode } from 'react'
import * as api from 'api/entityApi'
import { useToast } from 'components/common/Toast'
import { usePermissions } from 'hooks/usePermissions'
import { useEditAll } from 'components/edit/EditAllContext'

export interface InlineEditFieldProps {
  label: string
  required?: boolean
  /** Column name posted to `SaveInlineData` (the legacy `data-fieldname`). */
  fieldName: string
  /** Table name posted to `SaveInlineData` (the legacy `data-tablename`). */
  tableName: string
  entityId: string
  value: string
  onChange: (value: string) => void
  error?: string
  className?: string
  children: (props: { disabled: boolean }) => ReactNode
}

/**
 * The `.inlineEditbox` pattern from CreateLeadEdit: a field is read-only until the
 * pencil is clicked, then the tick saves just that column via `SaveInlineData`
 * and the cross reverts to the value the field had when editing started.
 */
export function InlineEditField({
  label,
  required,
  fieldName,
  tableName,
  entityId,
  value,
  onChange,
  error,
  className,
  children,
}: InlineEditFieldProps) {
  const toast = useToast()
  // Legacy equivalent: objPermId.Edit = ".btnEditInline, #btnSave"
  const permissions = usePermissions()
  // The page-level "Edit all fields" toggle unlocks this field too; then there is
  // no pencil and no per-field save — the page's Save posts the whole record.
  const editAll = useEditAll()
  const [editing, setEditing] = useState(false)
  const unlocked = editing || editAll
  const [saving, setSaving] = useState(false)
  const [snapshot, setSnapshot] = useState(value)

  const startEdit = () => {
    setSnapshot(value)
    setEditing(true)
  }

  const cancelEdit = () => {
    onChange(snapshot)
    setEditing(false)
  }

  const save = async () => {
    setSaving(true)
    try {
      await api.saveInlineField({ entityId, tableName, fieldName, fieldValue: value })
      toast.push('success', `${label} saved.`)
      setEditing(false)
    } catch (err) {
      toast.push('error', `Could not save ${label}: ${(err as Error).message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className={`form-group ${className ?? ''}`}>
      <label>
        {label}
        {required && <span className="red">*</span>}
      </label>

      <div className={`inline-editbox${unlocked ? ' is-editing' : ''}`}>
        {children({ disabled: !unlocked })}

        {!editAll && (
        <span className="inline-edit-btn">
          {editing ? (
            <>
              <button
                type="button"
                className={saving ? 'is-save saving' : 'is-save'}
                disabled={saving}
                onClick={save}
                title={`Save ${label}`}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
              </button>
              <button type="button" disabled={saving} onClick={cancelEdit} title="Cancel">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </>
          ) : (
            permissions.Edit && (
              <button type="button" onClick={startEdit} title={`Edit ${label}`}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
              </button>
            )
          )}
        </span>
        )}
      </div>

      {error && <div className="field-error">{error}</div>}
    </div>
  )
}
